import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Grid,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper
} from '@mui/material';
import {
  LocationOn,
  Business,
  AccountTree,
  TrendingUp,
  Assessment
} from '@mui/icons-material';
import { useRoleDefinitions } from '../../hooks/useRoleDefinitions';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';
import { fetchHierarchyDefinitions } from '../../services/hierarchyService';


/**
 * Component displaying role usage statistics and analytics
 * @param {Object} props - Component props
 * @returns {React.Component} Role usage statistics dashboard
 */
function RoleUsageStatistics() {
  const { roleDefinitions, clientId } = usePeopleAdmin();
  const { getActiveRoles, getLocationRoles, getOrgUnitRoles } = useRoleDefinitions();
  const [hierarchyLevels, setHierarchyLevels] = useState([]);

  const activeRoles = getActiveRoles();
  const locationRoles = getLocationRoles();
  const orgUnitRoles = getOrgUnitRoles();

  // Calculate statistics
  const totalRoles = roleDefinitions.length;
  const activeCount = activeRoles.length;
  const inactiveCount = totalRoles - activeCount;
  const utilizationRate = totalRoles > 0 ? (activeCount / totalRoles) * 100 : 0;

  // Fetch hierarchy definitions
  useEffect(() => {
    if (clientId) {
      fetchHierarchyDefinitions(clientId)
        .then(levels => {
          // Filter to only used levels and sort by order
          const usedLevels = levels
            .filter(level => level.isUsed !== false && ['ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'].includes(level.levelId))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          setHierarchyLevels(usedLevels);
        })
        .catch(error => {
          console.error('Failed to load hierarchy definitions:', error);
        });
    }
  }, [clientId]);

  // Calculate role counts by org level
  const getRoleCountByLevel = (levelId) => {
    return roleDefinitions.filter(role => 
      role.assignmentLevel === levelId && role.isActive !== false
    ).length;
  };

  // Prepare data for role type distribution
  const roleTypeData = [
    { 
      name: 'Location', 
      value: locationRoles.length, 
      icon: <LocationOn />,
      color: 'primary'
    },
    { 
      name: 'Org Unit', 
      value: orgUnitRoles.filter(r => r.roleType === 'ORGUNIT').length,
      icon: <Business />,
      color: 'secondary'
    },
    { 
      name: 'Both', 
      value: roleDefinitions.filter(r => r.roleType === 'BOTH').length,
      icon: <AccountTree />,
      color: 'info'
    }
  ].filter(item => item.value > 0);

  // Prepare data for role level distribution
  const roleLevelData = activeRoles.reduce((acc, role) => {
    const level = role.level || 'Unspecified';
    const existing = acc.find(item => item.level === level);
    if (existing) {
      existing.count++;
    } else {
      acc.push({ level, count: 1 });
    }
    return acc;
  }, []).sort((a, b) => {
    if (a.level === 'Unspecified') return 1;
    if (b.level === 'Unspecified') return -1;
    return a.level - b.level;
  });

  // Mock assignment data (will be populated from Location Admin integration)
  const assignmentData = activeRoles.map(role => ({
    name: role.roleCode,
    fullName: role.roleName,
    assignments: Math.floor(Math.random() * 50), // Mock data
    level: role.level
  })).sort((a, b) => b.assignments - a.assignments);

  const topAssignedRoles = assignmentData.slice(0, 5);

  return (
    <Grid container spacing={3}>
      {/* Summary Cards */}
      <Grid item xs={12} md={3}>
        <Card sx={{ p: 3, minHeight: 140 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {totalRoles}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Role Definitions
          </Typography>
          <Stack direction="row" spacing={1} mt={2}>
            <Chip 
              label={`${activeCount} Active`} 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '22px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
            <Chip 
              label={`${inactiveCount} Inactive`} 
              size="small" 
              color="default"
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '22px',
                '& .MuiChip-label': {
                  px: 1,
                  py: 0
                }
              }}
            />
          </Stack>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card sx={{ p: 3, minHeight: 140 }}>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {utilizationRate.toFixed(0)}%
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Utilization Rate
          </Typography>
          <Box mt={2}>
            <LinearProgress 
              variant="determinate" 
              value={utilizationRate} 
              sx={{ height: 8, borderRadius: 1 }}
              color="success"
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Active roles / Total roles
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card sx={{ p: 3, minHeight: 140 }}>
          <Typography variant="h4" fontWeight="bold" color="info.main">
            {locationRoles.length}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Location Roles
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Assignable at location level
          </Typography>
        </Card>
      </Grid>

      {/* Dynamic Org Level tiles */}
      {hierarchyLevels.map((level) => {
        const count = getRoleCountByLevel(level.levelId);
        return (
          <Grid item xs={12} md={3} key={level.levelId}>
            <Card sx={{ p: 3, minHeight: 140 }}>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {count}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {level.label} Roles
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Assignable at {level.label.toLowerCase()} level
              </Typography>
            </Card>
          </Grid>
        );
      })}

      {/* Role Type Distribution */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Role Type Distribution
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {roleTypeData.length > 0 ? (
            <Stack spacing={2}>
              {roleTypeData.map((item) => (
                <Box key={item.name}>
                  <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                    <Box color={`${item.color}.main`}>
                      {item.icon}
                    </Box>
                    <Typography variant="body1" fontWeight="medium" flex={1}>
                      {item.name}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color={`${item.color}.main`}>
                      {item.value}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(item.value / activeRoles.length) * 100}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={item.color}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {((item.value / activeRoles.length) * 100).toFixed(0)}% of active roles
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
              No role type data available
            </Typography>
          )}
        </Card>
      </Grid>

      {/* Role Level Distribution */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Role Level Hierarchy
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {roleLevelData.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      Level
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontWeight: 600,
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      Role Count
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      Distribution
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleLevelData.map((item) => (
                    <tr key={item.level}>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <Chip
                          label={`Level ${item.level}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '22px',
                            '& .MuiChip-label': {
                              px: 1,
                              py: 0
                            }
                          }}
                        />
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {item.count}
                        </Typography>
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(item.count / activeRoles.length) * 100}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="caption" color="text.secondary">
                              {`${Math.round((item.count / activeRoles.length) * 100)}%`}
                            </Typography>
                          </Box>
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
              No role level data available
            </Typography>
          )}
        </Card>
      </Grid>

      {/* Most Assigned Roles */}
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Top Assigned Roles
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {topAssignedRoles.length > 0 ? (
            <List>
              {topAssignedRoles.map((role, index) => (
                <ListItem key={role.name} divider={index < topAssignedRoles.length - 1}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip 
                          label={`Level ${role.level}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.75rem',
                            height: '22px',
                            '& .MuiChip-label': {
                              px: 1,
                              py: 0
                            }
                          }}
                        />
                        <Typography variant="body1" fontWeight="medium">
                          {role.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({role.name})
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Box mt={1}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(role.assignments / 50) * 100} 
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {role.assignments}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      assignments
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              No assignment data available. Integration with Location Admin pending.
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

export default RoleUsageStatistics;